// sharedList.component.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDataLayer } from "../../../Contexts/DataLayer.context.jsx";
import { usePlayer } from "../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../Contexts/navbar.items.context.jsx";
import { buildAuthedFormBody, getPlayersFromList } from "../../../utils/functions.ts";
import { jwtDecode } from "jwt-decode";
import DragBox2026 from "../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";

import "./sharedList.style.scss";

// ------------------------ constants ------------------------
const STORAGE_KEY_POS = "L2_BUTTON_LOCATION";
const DEFAULT_POS = { x: 57, y: 220 };
const LOCAL_STORAGE_LIST = "FBX_SELECTED_LIST";

const LISTS_ENDPOINT = "https://www.fbxchange.com/ReactPHP/listBox.php";
// keep your existing method (your backend may be MyLists now; this stays safe either way)
const LISTS_METHOD = "getSharedList2025";

// ------------------------ helpers ------------------------
const makeNoListRow = () => ({
  listName: "No List Data",
  listId: "0",
  listTotal: 0,
  theNumbers: [],
  displayName: "",
});

const isNoListRowArray = (arr) => {
  if (!Array.isArray(arr) || arr.length !== 1) return false;
  const r = arr[0] || {};
  return String(r.listId ?? r.listId) === "0" && String(r.listName ?? "").toLowerCase() === "no list data";
};

const SharedListBox = () => {
  // ✅ Context
  const { updateWhichOneLiner, updateListArray, sharedListArray, updateSharedListArray } = useDataLayer();

  const sharedRaw = Array.isArray(sharedListArray) ? sharedListArray : [];
  const hasNoListRow = isNoListRowArray(sharedRaw);

  // We render the placeholder row, but treat it as "no real lists" for logic.
  const sharedArray = hasNoListRow ? [] : sharedRaw;

  const updateSharedList = (arr) => {
    const next = Array.isArray(arr) ? arr : [];
    if (next.length === 0) updateSharedListArray([makeNoListRow()]);
    else updateSharedListArray(next);
  };

  // Player / navbar hooks
  const { setPlayerData, setSavedOriginalPlayerData, setActivePlayer, setPlayerID, setListLoadID } = usePlayer();
  const { updateSearchedListName, updateIsPillSelected, updateActiveSideButton } = useNavBar();

  // local UI state
  const [activeListID, setActiveListID] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [listOwner, setListOwner] = useState("");

  // COPY STATE
  const [showCopy, setShowCopy] = useState(false);
  const [copyListName, setCopyListName] = useState("");

  const tokenMemo = useMemo(() => {
    try {
      return localStorage.getItem("user2026") || "";
    } catch {
      return "";
    }
  }, []);

  // restore last active list id
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_LIST);
    if (saved) setActiveListID(saved);
  }, []);

  // ✅ On mount:
  // - If context already has REAL shared lists, do nothing.
  // - If context has only "No List Data", do nothing.
  // - If context is truly empty, fetch once and store in context.
  useEffect(() => {
    if (sharedRaw.length > 0) return;

    const loadSharedLists = async () => {
      try {
        const user = tokenMemo ? jwtDecode(tokenMemo) : null;

        const res = await fetch(LISTS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: buildAuthedFormBody({
            method: LISTS_METHOD,
            user: user ? JSON.stringify(user) : "{}",
          }).toString(),
        });

        if (!res.ok) {
          updateSharedList([]);
          return;
        }

        const data = await res.json();

        const incoming = Array.isArray(data)
          ? data
          : Array.isArray(data?.sharedList)
            ? data.sharedList
            : [];

        updateSharedList(incoming);
      } catch (err) {
        console.error("Failed to load shared lists", err);
        updateSharedList([]);
      }
    };

    loadSharedLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenMemo, sharedRaw.length]);

  // Activate a list
  const activateList = async (listId) => {
    if (String(listId) === "0") return;

    try {
      localStorage.setItem(LOCAL_STORAGE_LIST, listId);
      setActiveListID(listId);
      updateWhichOneLiner(null);

      const theUser = tokenMemo ? jwtDecode(tokenMemo) : null;
      const newPlayers = await getPlayersFromList(listId, theUser?.team, theUser?.scout);

      setPlayerData(newPlayers);
      setSavedOriginalPlayerData(newPlayers);
      setPlayerID(newPlayers?.[0]?.FBXID || "");
      setActivePlayer(newPlayers?.[0] || []);
      setListLoadID(Math.floor(Math.random() * (1000000 - 1000 + 1)) + 1000);

      const found = (sharedArray || []).find((i) => String(i.listId) === String(listId));
      if (found?.listName) updateSearchedListName("Current List: " + found.listName);

      updateIsPillSelected(false);
      updateActiveSideButton(null);
    } catch (err) {
      console.error("Failed to activate list:", err);
    }
  };

  const removeList = (listId, displayName) => {
    if (String(listId) === "0") return;
    setListOwner(displayName || "");
    setActiveListID(listId);
    setShowDelete(true);
  };

  const removeEmitHelper = (inval) => {
    if (String(inval) === "0") return;

    try {
      const token = localStorage.getItem("user2026");
      if (!token) return;

      const decoded = jwtDecode(token);
      const scout = decoded.scout;
      const displayName = decoded.displayName;

      fetch("https://www.fbxchange.com/ReactPHP/listBox.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: buildAuthedFormBody({
          method: "removeMeFromList",
          scout,
          displayName,
          inval,
        }).toString(),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data && data.success) {
            const next = (sharedArray || []).filter((item) => String(item.listId) !== String(inval));
            updateSharedList(next);
            setShowDelete(false);
          }
        });
    } catch (err) {
      console.error("Error removing from list:", err);
    }
  };

  const copyThisList = (listId, newName) => {
    if (String(listId) === "0") return;

    if (!newName || newName.trim() === "") {
      alert("Please provide a valid name for the copied list.");
      return;
    }

    try {
      const token = localStorage.getItem("user2026");
      if (!token) return;

      const decoded = jwtDecode(token);
      const scout = decoded.scout;
      const displayName = decoded.displayName;
      const team = decoded.team;

      fetch("https://www.fbxchange.com/ReactPHP/listBox.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: buildAuthedFormBody({
          method: "copySharedList",
          scout,
          displayName,
          listId,
          team,
          newListName: newName,
        }).toString(),
      })
        .then((res) => res.json())
        .then((data) => {
          updateListArray(data); // refresh L1 list array
          setShowCopy(false);
        });
    } catch (err) {
      console.error("Error copying the list:", err);
    }
  };

  // What to render:
  const rowsToRender = hasNoListRow ? [makeNoListRow()] : sharedArray;

  return (
    <DragBox2026
      storageKey={STORAGE_KEY_POS}
      defaultPos={DEFAULT_POS}
      title="My Shared Lists"
      countText={`[${Array.isArray(sharedArray) ? sharedArray.length : 0} Lists]`}
      showReset={true}
      // optional extra logic when reset is clicked:
      onReset={() => {
        // nothing required here; DragBox2026 already resets position + clears localStorage
      }}
      showClose={true}
      // set to whatever your app uses for "closed"; if you use '' instead of null, change it here:
      closeValue={null}
      minWidth={700}
      maxWidth={700}
      zIndex={500}
    >
      <div style={{ height: "100%", width: "100%", minHeight: 400, maxHeight: 700, overflowY: "auto" }}>
        {showDelete ? (
          <div
            className="py-2 mb-0 text-center d-flex flex-column align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(235,160,168,.3)" }}
          >
            <div className="fs-5 mb-2">Are you sure you want to remove yourself from this shared list?</div>

            <div className="alert alert-light p-3 shadow-sm text-start" style={{ maxWidth: 720, width: "90%" }}>
              <div className="text-center mb-4 text-primary fw-bold">
                Shared Lists are Active Lists, Meaning any changes made by {listOwner} to this list will be reflected for
                you as well.
              </div>

              <ul className="mb-3 ps-4">
                <li className="mb-2">
                  This list was shared to you by {listOwner} and if you remove it, you will no longer have access to this
                  list.
                </li>
                <li className="mb-2">Your Removal of this list will be logged to {listOwner} List Log.</li>
                <li className="mb-2">You will not longer be able to Copy this List.</li>
              </ul>

              <div className="d-flex justify-content-center gap-3 mt-3 mb-2">
                <button className="btn btn-outline-danger" onClick={() => removeEmitHelper(activeListID)}>
                  Yes Remove This List
                </button>
                <button className="btn btn-outline-success" onClick={() => setShowDelete(false)}>
                  No do not remove me
                </button>
              </div>
            </div>
          </div>
        ) : showCopy ? (
          <div
            className="py-2 mb-0 text-center d-flex flex-column align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(40,167,69,.15)" }}
          >
            <div className="fs-5 mb-2">Copy this shared list?</div>

            <div className="alert alert-light p-3 shadow-sm text-start" style={{ maxWidth: 720, width: "90%" }}>
              <div className="text-center mb-3 fw-bold">Choose a name for your new list copy</div>

              <label htmlFor="copyName" className="form-label fw-semibold">
                New List Name
              </label>
              <input
                id="copyName"
                className="form-control"
                type="text"
                placeholder="Enter copy name…"
                value={copyListName}
                onChange={(e) => setCopyListName(e.target.value)}
              />

              <div className="d-flex justify-content-center gap-3 mt-3 mb-1">
                <button
                  className="btn btn-outline-success col-16"
                  onClick={() => {
                    copyThisList(activeListID, copyListName);
                    setShowCopy(false);
                  }}
                >
                  Yes, Copy List
                </button>

                <button className="btn btn-outline-danger col-16" onClick={() => setShowCopy(false)}>
                  No, DO NOT copy list
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {rowsToRender.map((item) => {
              const isPlaceholder = String(item.listId) === "0";
              const isActive = String(item.listId) === String(activeListID);

              return (
                <div
                  key={String(item.listId)}
                  className={`listItem d-flex align-items-center justify-content-start fs-7 px-2 gap-2 ${
                    !isPlaceholder && isActive ? "alert-info" : ""
                  }`}
                  title={item.listName}
                  style={{ opacity: isPlaceholder ? 0.75 : 1 }}
                >
                  <div
                    className="col-24 list-name text-truncate"
                    onClick={() => {
                      if (!isPlaceholder) activateList(item.listId);
                    }}
                    style={{ cursor: isPlaceholder ? "default" : "pointer" }}
                  >
                    {item.listName}
                    {!isPlaceholder ? ` [${item.displayName ?? "NA"}]]` : ""}
                  </div>

                  <div className="ms-auto col-24 d-flex align-items-center justify-content-end gap-2">
                    <div className="col-12">
                      <button
                        className="btn btn-sm btn-outline-danger w-100"
                        disabled={isPlaceholder}
                        onClick={() => removeList(item.listId, item.displayName)}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="col-12">
                      <button
                        className="btn btn-sm btn-outline-purple w-100"
                        disabled={isPlaceholder}
                        onClick={() => {
                          if (isPlaceholder) return;
                          setActiveListID(item.listId);
                          setCopyListName(item.listName ? `${item.listName} (Copy)` : "");
                          setShowCopy(true);
                        }}
                      >
                        Copy
                      </button>
                    </div>

                    <div
                      className="col-10"
                      onClick={() => {
                        if (!isPlaceholder) activateList(item.listId);
                      }}
                      style={{ cursor: isPlaceholder ? "default" : "pointer" }}
                    >
                      <div className="btn btn-sm btn-primary w-100">{isPlaceholder ? 0 : item.listTotal ?? 0}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </DragBox2026>
  );
};

export default SharedListBox;
